/**
 * `AIRealtimePersonalityCatalog.ts` — port of Unity's `AI/AIRealtimePersonalityCatalog.cs`.
 *
 * Loads the available personalities from `/AI/personalities/index.json` (+ the referenced `*.md`
 * files) and registers them on `agentPrompts`. A missing/empty catalog falls back to the compiled
 * bootstrap personalities. Must be awaited before the realtime session connects so the first
 * prompt's voice rules, voice, and appearance are complete.
 *
 * Web deviation: the manifest is REQUIRED (like Unity's Android path) — the browser cannot
 * directory-scan the public root, so a personality file not listed in `index.json` is invisible.
 */

import { AIRealtimePersonalityDefinition } from "./aiRealtimeContentTypes";
import { tryParseFrontmatter } from "./aiRealtimeMarkdownFrontmatter";
import { setPersonalityDefinitions } from "./agentPrompts";
import { normalizeAgentPromptFileName } from "@models/data/agentPromptsDto";
import type { AgentPromptSource } from "./IAIPromptStoreModel";

import { publicUrl } from "@core/publicUrl";
const K_PERSONALITIES_ROOT = publicUrl("AI/personalities");
const K_MANIFEST_PATH = `${K_PERSONALITIES_ROOT}/index.json`;

interface PersonalityManifestEntry {
  readonly id?: string;
  readonly displayName?: string;
  readonly description?: string;
  readonly appearance?: string;
  readonly voice?: string;
  readonly file?: string;
}

let s_loadTask: Promise<void> | null = null;
/** The definitions last installed, so the debug panel can render what the agent has. */
let s_loaded: AIRealtimePersonalityDefinition[] = [];

/**
 * Idempotent cached load-and-register (mirrors Unity's `EnsureLoadedAsync`),
 * with a `force` escape hatch for a publish or reset.
 *
 * The reset seam is what makes a personality publishable at all: with a bare
 * `s_loadTask ??=` the first load of the session was permanent, so a newly
 * published personality could not take effect until a page reload.
 */
export function ensurePersonalitiesLoadedAsync(
  prompts?: AgentPromptSource | null,
  force = false,
): Promise<void> {
  if (force) s_loadTask = null;
  return (s_loadTask ??= loadAndRegisterAsync(prompts));
}

/** The personalities currently installed — published copies or shipped files. */
export function getLoadedPersonalities(): ReadonlyArray<AIRealtimePersonalityDefinition> {
  return s_loaded;
}

async function loadAndRegisterAsync(prompts?: AgentPromptSource | null): Promise<void> {
  try {
    const personalities = await loadAvailablePersonalitiesAsync(prompts);
    if (personalities.length === 0) {
      console.warn(
        "[AIRealtimePersonalityCatalog] No personalities found in /AI/personalities; using bootstrap personalities.",
      );
    }

    s_loaded = personalities;
    setPersonalityDefinitions(personalities);
  } catch (e) {
    console.warn("[AIRealtimePersonalityCatalog] Failed to load personalities:", e);
    s_loaded = [];
    setPersonalityDefinitions(null);
  }
}

export async function loadAvailablePersonalitiesAsync(
  prompts?: AgentPromptSource | null,
): Promise<AIRealtimePersonalityDefinition[]> {
  let entries: PersonalityManifestEntry[];
  try {
    const response = await fetch(K_MANIFEST_PATH, { cache: "no-cache" });
    if (!response.ok) {
      return [];
    }

    const manifest = (await response.json()) as { personalities?: PersonalityManifestEntry[] };
    entries = manifest.personalities ?? [];
  } catch (e) {
    console.warn(`[AIRealtimePersonalityCatalog] Failed to parse ${K_MANIFEST_PATH}:`, e);
    return [];
  }

  const personalities: AIRealtimePersonalityDefinition[] = [];
  for (const entry of entries) {
    if (!entry || !entry.file || !entry.file.trim()) {
      continue;
    }

    const personality = await tryLoadPersonalityAsync(entry, prompts);
    if (personality) {
      personalities.push(personality);
    }
  }

  return personalities;
}

async function tryLoadPersonalityAsync(
  entry: PersonalityManifestEntry,
  prompts?: AgentPromptSource | null,
): Promise<AIRealtimePersonalityDefinition | null> {
  const fileName = normalizeAgentPromptFileName(entry.file);
  if (!fileName) {
    return null;
  }

  // The deployment's published copy stands in for the shipped file, keyed on the
  // BASENAME — the same value the fetch below uses.
  const published = prompts?.getPromptText("personalities", fileName) ?? null;

  let markdown: string;
  if (published !== null) {
    markdown = published;
  } else {
    try {
      const response = await fetch(`${K_PERSONALITIES_ROOT}/${fileName}`, { cache: "no-cache" });
      if (!response.ok) {
        console.warn(
          `[AIRealtimePersonalityCatalog] Failed to read '${K_PERSONALITIES_ROOT}/${fileName}': HTTP ${response.status}`,
        );
        return null;
      }

      markdown = await response.text();
    } catch (e) {
      console.warn(`[AIRealtimePersonalityCatalog] Failed to read '${K_PERSONALITIES_ROOT}/${fileName}':`, e);
      return null;
    }
  }

  if (!markdown.trim()) {
    return null;
  }

  const { fields, body } = tryParseFrontmatter(markdown);
  const baseName = fileName.replace(/\.md$/i, "");

  const rawId = firstNonEmpty(entry.id, fields["id"], baseName);
  const id = rawId ? rawId.toLowerCase() : "";
  const displayName = firstNonEmpty(entry.displayName, fields["name"], fields["displayname"], baseName) ?? "";
  const appearance = firstNonEmpty(entry.appearance, fields["appearance"]);
  const voice = firstNonEmpty(entry.voice, fields["voice"]);
  const description = firstNonEmpty(entry.description, fields["description"]);

  return new AIRealtimePersonalityDefinition(id, displayName, fileName, body, appearance, voice, description);
}

function firstNonEmpty(...values: Array<string | undefined | null>): string | null {
  for (const value of values) {
    if (value && value.trim()) {
      return value.trim();
    }
  }

  return null;
}
